import React, { useMemo, useRef, useState, useCallback, useLayoutEffect, useEffect } from "react";
import {
  capacityLabel,
  cleanTruckLabel,
  getTruckImage,
  vehiclePriceTip,
} from "./helpers";

function SiblingsRow({ siblings, siblingsLabel, onSelectSibling }) {
  const rowRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [multiRow, setMultiRow] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const collapsible = siblingsLabel === "Other sizes, same body type";
  const siblingKey = siblings.map((s) => s.type).join(",");

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row || !collapsible) {
      setMultiRow(false);
      setCollapsedHeight(null);
      setHiddenCount(0);
      return;
    }
    const items = [...row.children];
    if (items.length < 2) {
      setMultiRow(false);
      setCollapsedHeight(null);
      setHiddenCount(0);
      return;
    }
    const firstTop = items[0].offsetTop;
    let firstRowCount = 0;
    for (const item of items) {
      if (item.offsetTop > firstTop + 2) break;
      firstRowCount++;
    }
    const wraps = firstRowCount < items.length;
    setMultiRow(wraps);
    setHiddenCount(wraps ? items.length - firstRowCount : 0);
    if (wraps) {
      const firstRowItems = items.slice(0, firstRowCount);
      const bottom = Math.max(
        ...firstRowItems.map((el) => el.offsetTop + el.offsetHeight),
      );
      setCollapsedHeight(bottom - row.offsetTop);
    } else {
      setCollapsedHeight(null);
    }
  }, [collapsible]);

  useLayoutEffect(() => {
    measure();
  }, [measure, siblingKey]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(row);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  useEffect(() => {
    setExpanded(false);
  }, [siblingsLabel, siblingKey]);

  const isCollapsed = multiRow && !expanded;

  return (
    <>
      <div
        ref={rowRef}
        className={
          "lp2-vdetail-siblings-row" +
          (isCollapsed ? " lp2-vdetail-siblings-row--collapsed" : "")
        }
        role="group"
        aria-label={siblingsLabel}
        style={
          isCollapsed && collapsedHeight
            ? { maxHeight: collapsedHeight }
            : undefined
        }
      >
        {siblings.map((v) => {
          const sibName = v.loadgroup || cleanTruckLabel(v.type);
          const sibBody = (v.bodylabel || "").replace(/\s*body$/i, "").trim();
          const sibCap = capacityLabel(v.tonnes);
          return (
            <button
              type="button"
              key={v.type}
              className="lp2-vdetail-sibling"
              onClick={() => onSelectSibling(v.type)}
              title={`${sibName}${sibCap ? ` · ${sibCap}` : ""}`}
            >
              <img
                src={getTruckImage(v.type)}
                alt=""
                loading="lazy"
                width={72}
                height={48}
              />
              <span className="lp2-vdetail-sibling-name">{sibBody || sibName}</span>
            </button>
          );
        })}
      </div>
      {multiRow && (
        <button
          type="button"
          className={
            "lp2-vdetail-siblings-toggle" + (expanded ? " is-open" : "")
          }
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          <span>
            {expanded
              ? "Show fewer sizes"
              : `Show ${hiddenCount} more size${hiddenCount === 1 ? "" : "s"}`}
          </span>
          <i className="mdi mdi-chevron-down" aria-hidden="true" />
        </button>
      )}
    </>
  );
}

/**
 * Detailed selected-vehicle card. Shown after the customer picks a vehicle in
 * the picker, just above the email / mobile contact fields. Gives them the
 * confidence to proceed by showing exactly what they're getting: capacity,
 * example loads, similar vehicle models, and what's included.
 */
const VehicleDetail = ({ vehicle, vehicletypes = [], onSelectSibling, onChange }) => {
  const { siblings, siblingsLabel } = useMemo(() => {
    if (!vehicle) return { siblings: [], siblingsLabel: "" };

    const sellable = (vehicletypes || []).filter(
      (v) => v.sell && v.type !== vehicle.type,
    );
    if (!sellable.length) return { siblings: [], siblingsLabel: "" };

    if (vehicle.loadgroup) {
      const sameSize = sellable.filter((v) => v.loadgroup === vehicle.loadgroup);
      if (sameSize.length) {
        return {
          siblings: sameSize,
          siblingsLabel: "Other body types at this size",
        };
      }
    }

    const body = vehicle.bodylabel || "standard";
    const seen = new Set();
    const sameBody = sellable.filter((v) => {
      if ((v.bodylabel || "standard") !== body) return false;
      const group = v.loadgroup || v.type;
      if (seen.has(group)) return false;
      seen.add(group);
      return true;
    });
    sameBody.sort(
      (a, b) => (parseFloat(a.tonnes) || 0) - (parseFloat(b.tonnes) || 0),
    );

    return {
      siblings: sameBody,
      siblingsLabel: "Other sizes, same body type",
    };
  }, [vehicle, vehicletypes]);

  if (!vehicle) return null;

  const name = vehicle.loadgroup || cleanTruckLabel(vehicle.type);
  const body = (vehicle.bodylabel || "").replace(/\s*body$/i, "").trim();
  const capacity = capacityLabel(vehicle.tonnes);
  const examples = vehicle.tip?.examples;
  const models = (vehicle.realWorldExamples || []).slice(0, 4);
  const from = vehiclePriceTip(vehicle, 1);

  const facts = [
    capacity && {
      icon: "mdi-weight-kilogram",
      label: "Capacity",
      value: capacity,
    },
    body && { icon: "mdi-truck-outline", label: "Body type", value: body },
    {
      icon: "mdi-account-tie-hat",
      label: "Driver",
      value: "Included",
    },
    {
      icon: "mdi-shield-check",
      label: "Insurance",
      value: "Goods in transit",
    },
    {
      icon: "mdi-fuel",
      label: "Fuel",
      value: "Included",
    },
    {
      icon: "mdi-cash",
      label: "Indicative price",
      value: `from R${from}`,
    },
  ].filter(Boolean);

  return (
    <section
      className="lp2-vdetail"
      id="lp2-vehicledetail"
      aria-label="Selected vehicle details"
    >
      <header className="lp2-vdetail-head">
        <span className="lp2-vdetail-eyebrow">
          <i className="mdi mdi-check-circle" aria-hidden="true" />
          Selected vehicle
        </span>
        {onChange && (
          <button
            type="button"
            className="lp2-vdetail-change"
            onClick={onChange}
          >
            <i className="mdi mdi-pencil-outline" aria-hidden="true" />
            Change
          </button>
        )}
      </header>

      <div className="lp2-vdetail-main">
        <div className="lp2-vdetail-media">
          <img
            src={getTruckImage(vehicle.type)}
            alt={`${name} with driver`}
            loading="lazy"
          />
        </div>

        <div className="lp2-vdetail-body">
          <h3 className="lp2-vdetail-title">{name}</h3>
          {capacity && (
            <p className="lp2-vdetail-sub">
              {capacity}
              {body && <> · {body} body</>}
            </p>
          )}

          {examples && (
            <p className="lp2-vdetail-examples">
              <i className="mdi mdi-lightbulb-on-outline" aria-hidden="true" />
              <span>
                <strong>Best for:</strong> {examples}
              </span>
            </p>
          )}

          {models.length > 0 && (
            <p className="lp2-vdetail-models">
              <span className="lp2-vdetail-models-label">Similar models:</span>{" "}
              {models.join(" · ")}
            </p>
          )}
        </div>
      </div>

      <ul className="lp2-vdetail-facts">
        {facts.map((f) => (
          <li key={f.label} className="lp2-vdetail-fact">
            <i className={"mdi " + f.icon} aria-hidden="true" />
            <span className="lp2-vdetail-fact-label">{f.label}</span>
            <span className="lp2-vdetail-fact-value">{f.value}</span>
          </li>
        ))}
      </ul>

      {siblings.length > 0 && onSelectSibling && (
        <div className="lp2-vdetail-siblings">
          <span className="lp2-vdetail-siblings-label">{siblingsLabel}</span>
          <SiblingsRow
            siblings={siblings}
            siblingsLabel={siblingsLabel}
            onSelectSibling={onSelectSibling}
          />
        </div>
      )}
    </section>
  );
};

export default VehicleDetail;
